import React, {
  FunctionComponent,
  useState,
  ChangeEvent,
  useCallback,
} from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';

import { SlideIn, SubmitButton } from 'components';
import { FormGroup, InputFormat } from 'components/chroma';

import {
  CoverType,
  FunnelType,
  YesNoAnswer,
  YES_NO_OPTIONS,
} from 'scenes/common/types';
import { SegmentedControlQuestion } from 'scenes/common/components/Questions';

import { MotorCoverType } from 'scenes/motor/types';
import FlybuysModal from './FlybuysModal';
import {
  FLYBUYS_NUMBER_LENGTH,
  FLYBUYS_PREFIX,
  validateFlybuysNumber,
} from './service/flybuys';
import { FlybuysHelpText } from './FlybuysHelpText';

const HAS_FLYBUYS = 'hasFlybuys';
const FLYBUYS_NUMBER = 'flybuysNumber';

export const QUESTION_MEMBERSHIP = 'Are you a Flybuys member?';
export const QUESTION_NUMBER = 'Flybuys membership number';
export const FLYBUYS_NUMBER_HELP_LINK = 'Where is my Flybuys number?';
export const INVALID_NUMBER_ERROR = 'This is not a valid Flybuys number';

const FlybuysStep: FunctionComponent<{
  submitButtonCaption: string;
  onSubmit: SubmitHandler<Record<string, unknown>>;
  defaultFlybuysNumberStateValue: string;
  defaultHasFlybuysStateValue: YesNoAnswer;
  onUpdateFlybuys?: Function;
  coverType: MotorCoverType | CoverType;
  productType: FunnelType;
}> = ({
  onSubmit,
  submitButtonCaption,
  defaultHasFlybuysStateValue,
  defaultFlybuysNumberStateValue,
  onUpdateFlybuys,
  coverType,
  productType,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    getValues,
    trigger,
    watch,
  } = useForm({
    mode: 'onChange',
  });
  const [showModal, setShowModal] = useState(false);
  const watchedHasFbNumber = watch(HAS_FLYBUYS, defaultHasFlybuysStateValue);

  const handleHasFlybuysNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function) => {
      onChange(event);
      if (onUpdateFlybuys) {
        onUpdateFlybuys(event.target.value);
      }
      trigger();
    },
    [trigger, onUpdateFlybuys]
  );

  const handleFlybuysNumberChange = useCallback(
    (val: string, onChange: Function): void => {
      const flybuysNumberWithoutPrefix = val.replace(FLYBUYS_PREFIX, '');
      onChange(flybuysNumberWithoutPrefix);
      if (onUpdateFlybuys) {
        onUpdateFlybuys(watchedHasFbNumber, flybuysNumberWithoutPrefix);
      }
    },
    [onUpdateFlybuys, watchedHasFbNumber]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SegmentedControlQuestion
        errors={errors}
        control={control}
        stateKey={HAS_FLYBUYS}
        options={YES_NO_OPTIONS}
        defaultValue={defaultHasFlybuysStateValue}
        changeHandler={handleHasFlybuysNumberChange}
        question={QUESTION_MEMBERSHIP}
        helpText={
          <FlybuysHelpText productType={productType} coverType={coverType} />
        }
      />

      <Controller
        control={control}
        name={FLYBUYS_NUMBER}
        defaultValue={defaultFlybuysNumberStateValue}
        rules={{
          validate: {
            validFbNumber: (value) => {
              if (getValues(HAS_FLYBUYS) === YesNoAnswer.No) return true;
              const flybuysNumber = `${FLYBUYS_PREFIX}${value}`;
              if (flybuysNumber.length !== FLYBUYS_NUMBER_LENGTH) return false;
              return validateFlybuysNumber(flybuysNumber)
                ? true
                : INVALID_NUMBER_ERROR;
            },
          },
        }}
        render={({ onChange, value }) =>
          watchedHasFbNumber === YesNoAnswer.Yes ? (
            <SlideIn>
              <FormGroup
                id={FLYBUYS_NUMBER}
                label={QUESTION_NUMBER}
                error={
                  hooksForm.dirtyFields[FLYBUYS_NUMBER]
                    ? errors[FLYBUYS_NUMBER]?.message || ''
                    : ''
                }
                helpLink={{
                  id: 'helpLink',
                  text: FLYBUYS_NUMBER_HELP_LINK,
                  ariaLabel: 'Where is my Flybuys number? Opens dialog',
                  onClick: () => {
                    setShowModal(true);
                  },
                }}
              >
                <InputFormat
                  id={FLYBUYS_NUMBER}
                  name={FLYBUYS_NUMBER}
                  placeholder="Flybuys number"
                  ariaLabel="Flybuys membership number. Please enter the 10-digit number after '6008 94'"
                  value={defaultFlybuysNumberStateValue}
                  size={4}
                  format="6008 94## #### ####"
                  allowEmptyFormatting
                  onChange={(val: string) =>
                    handleFlybuysNumberChange(val, onChange)
                  }
                  valid={Boolean(value) && !errors[FLYBUYS_NUMBER]}
                  error={Boolean(value) && Boolean(errors[FLYBUYS_NUMBER])}
                  type="text"
                />
              </FormGroup>
            </SlideIn>
          ) : (
            <></>
          )
        }
      />
      <SubmitButton enabled={hooksForm.isValid}>
        {submitButtonCaption}
      </SubmitButton>
      <FlybuysModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </form>
  );
};

export default FlybuysStep;
